<template>
  <vz-dialog
    ref="dialog"
    :name="modalName"
    big
    class="vz-specific-loading-modal"
    title="Спец. требования и транспорт"
    @show="onShow"
  >
    <div v-if="!loading" class="vz-specific-loading-modal-wrapper">
      <div>
        <div class="requirements-modal-title">
          Вид транспорта
        </div>
        <vz-select
          :modelValue="copyValue.type"
          :items="transportTypes"
          with-tooltips
          :tooltipsInstantHide="true"
          @change="transportTypeChanged"
        />
        <vz-requirements-checkbox
          v-for="checkbox in transportCheckBoxes"
          :key="checkbox.value"
          class="mt-10"
          :modelValue="checkbox"
          :tooltipsInstantHide="true"
          @change="onChanged(checkbox.value, $event)"
        />
        <div class="requirements-modal-title">
          Высота пандуса
        </div>
        <vz-select
          :modelValue="rampValue"
          :items="rampData"
          with-tooltips
          :tooltipsInstantHide="true"
          @change="rampChanged"
        />
        <div class="requirements-modal-title">
          Ограничение по высоте
        </div>
        <vz-select
          :modelValue="heightValue"
          :items="heightData"
          with-tooltips
          :tooltipsInstantHide="true"
          @change="heightChanged"
        />
        <div class="requirements-modal-title">
          Ширина кузова
        </div>
        <vz-select
          :modelValue="widthValue"
          :items="widthData"
          @change="widthChanged"
        />
      </div>
      <div>
        <div class="requirements-modal-title">
          Водитель
        </div>
        <vz-requirements-checkbox
          v-for="checkbox in driverCheckBoxes"
          :key="checkbox.value"
          class="mt-10"
          :modelValue="checkbox"
          :tooltipsInstantHide="true"
          @change="onChanged(checkbox.value, $event)"
        />
        <div class="requirements-modal-title">
          Дополнительно
        </div>
        <vz-requirements-checkbox
          v-for="checkbox in additionalCheckBoxes"
          :key="checkbox.value"
          class="mt-10"
          :modelValue="checkbox"
          :tooltipsInstantHide="true"
          @change="onChanged(checkbox.value, $event)"
        />
      </div>
    </div>
    <vz-specific-loading-modal-skeleton v-else />
    <template #footer>
      <vz-button
        variant="primary"
        class="flex-center"
        big
        @click="save"
      >
        Применить
      </vz-button>
    </template>
  </vz-dialog>
</template>

<script setup lang="ts">
import {useOrderStore} from '~/stores/order';
import {OrderSpecialRequirement, SelectItem, SpecialRequirementsCheckbox} from '~/types/types';
import {getOrderSpecificLoadingList} from '@/api/order';
import VzRequirementsCheckbox
  from '~/components/personal/order/special-requirements/vz-requirements-checkbox.vue';
import VzDialog from '~/components/ui-kit/vz-dialog.vue';
import VzSpecificLoadingModalSkeleton from '~/components/modals/vz-specific-loading/vz-specific-loading-modal-skeleton.vue';
import {storeToRefs} from 'pinia';
import {useSpecificLoadingStore} from '~/stores/specific-loading';
import VzSelect from "~/components/ui-kit/vz-select.vue";

const emit = defineEmits(['save'])

const props = defineProps({
  directionTo: {
    type: Boolean,
    default: false
  }
})

const { $hideDialog } = useNuxtApp()
const orderStore = useOrderStore()
const specificLoadingStore = useSpecificLoadingStore()
const { specificLoadingList } = storeToRefs(specificLoadingStore)
const { getSpecificLoadingByCode, getSpecificLoadingListByCategory } = specificLoadingStore
const copyValue = ref({} as OrderSpecialRequirement)
const visibleProperties = ref([] as string[])
const loading = ref(false)

const directionName = computed(() => {
  return props.directionTo ? 'destination' : 'dispatch'
})

const modalName = computed(() => {
  return `specific-loading-${directionName.value}`
})

const rampData = computed((): SelectItem[] => {
  return [
    {
      value: 'ramp_0',
      title: 'Не имеет значения',
      tooltip: 'Особых требований нет'
    },
    ...specificLoadingList.value
      .filter(item => item.category_code === 'rampHeight')
      .map(item => ({
        value: item.code,
        title: item.name,
        tooltip: item.hint
      }))
  ]
})

const heightData = computed((): SelectItem[] => {
  return [
    {
      value: 'bodyHeight_0',
      title: 'Не имеет значения',
      tooltip: 'Особых требований нет'
    },
    ...specificLoadingList.value
      .filter(item => item.category_code === 'heightLimit')
      .map(item => ({
        value: item.code,
        title: item.name,
        tooltip: item.hint
      }))
  ]
})

const widthData = computed((): SelectItem[] => {
  return [
    {
      value: 'width0',
      title: 'Не имеет значения',
    },
    ...specificLoadingList.value
      .filter(item => item.category_code === 'bodyWidth')
      .map(item => ({
        value: item.code,
        title: item.name,
        tooltip: item.hint
      }))
  ]
})

const orderSpecificLoading = computed(() => {
  return orderStore.orderPrice?.specificLoading?.[directionName.value]
})

const transportTypes = computed((): SelectItem[] => {
  return filterArray([
    {
      value: 'default',
      title: 'Не имеет значения',
      tooltip: 'Не имеет значения'
    },
    ...getSpecificLoadingListByCategory('standard')
      .filter(item => !item.inclusible)
      .map(item => ({
        value: item.code,
        title: item.name + (orderSpecificLoading.value?.[item.code]?.price?.total
          ? ` (${orderSpecificLoading.value?.[item.code]?.price?.total} ${orderStore.orderPriceCurrency.currency})`
          : ''),
        tooltip: item.hint
      }))
  ])
})

const transportCheckBoxes = computed((): SpecialRequirementsCheckbox[] => {
  const emptyMachineCostText = orderStore.orderPrice?.specificLoading?.[directionName.value]?.emptyMachine?.price?.total
    ? `(${orderStore.orderPrice?.specificLoading?.[directionName.value]?.emptyMachine?.price?.total || 0} ${orderStore.orderPriceCurrency.currency})`
    : ''
  const emptyMachineItem = getSpecificLoadingByCode('emptyMachine')
  const rearLoadingItem = getSpecificLoadingByCode('rearLoading')

  return filterArray([
    {
      value: 'emptyMachine',
      active: Boolean(copyValue.value.emptyMachine),
      title: `${emptyMachineItem?.name} ${emptyMachineCostText}`,
      tooltip: emptyMachineItem?.hint
    },
    {
      value: 'rearLoading',
      active: Boolean(copyValue.value.rearLoading),
      title: rearLoadingItem?.name,
      tooltip: rearLoadingItem?.hint,
    }
  ])
})

const additionalCheckBoxes = computed(() => {
  const data = ['sanitaryPassport', 'pallets', 'fixingStraps'].map((key) => {
    const specificLoadingItem = getSpecificLoadingByCode(key)
    return {
      value: key,
      active: Boolean(copyValue.value[key as 'sanitaryPassport' | 'pallets' | 'fixingStraps']),
      title: specificLoadingItem.name,
      tooltip: specificLoadingItem.hint
    }
  }) as SpecialRequirementsCheckbox[]

  return filterArray(data)
})

const driverCheckBoxes = computed((): SpecialRequirementsCheckbox[] => {
  const medicalCardItem = getSpecificLoadingByCode('medicalCard')

  return filterArray([
    {
      value: 'medicalCard',
      active: Boolean(copyValue.value.medicalCard),
      title: medicalCardItem.name,
      tooltip: medicalCardItem.hint
    },
    ...getSpecificLoadingListByCategory('driver')
      .map(item => ({
        value: item.code,
        active: Boolean(copyValue.value[item.code]),
        title: item.name,
        tooltip: item.hint
      })) as any
  ])
})

const locationZoneGuid = computed(() => {
  return (props.directionTo ? orderStore.destinationLocation?.zone_guid : orderStore.dispatchLocation?.zone_guid) || ''
})

const value = computed(() => {
  return orderStore.orderForm.specialRequirements[directionName.value]
})

const rampValue = computed(() => {
  return rampData.value.find(record => copyValue.value[record.value] === true)?.value || rampData.value[0].value
})

const heightValue = computed(() => {
  return heightData.value.find(record => copyValue.value[record.value] === true)?.value || heightData.value[0].value
})

const widthValue = computed(() => {
  return widthData.value.find(record => copyValue.value[record.value] === true)?.value || widthData.value[0].value
})

function close() {
  $hideDialog({ name: modalName.value })
}

function save () {
  close()
  emit('save', copyValue.value)
}

function normalizeSpecificLoading() {
  if (driverCheckBoxes.value.length > 0) {
    driverCheckBoxes.value.forEach((item) => {
      if (copyValue.value[item.value] === undefined) {
        copyValue.value[item.value] = false as never
      }
    })
  }
}

function onChanged(property: keyof OrderSpecialRequirement, value: OrderSpecialRequirement[keyof OrderSpecialRequirement]): void {
  copyValue.value[property] = value as never
}

function transportTypeChanged(data: SelectItem) {
  copyValue.value.type = String(data.value)
}

function selectChange (records: SelectItem[], property: string): void {
  records.forEach((record) => {
    if (copyValue.value[record.value] !== undefined) {
      onChanged(record.value as keyof OrderSpecialRequirement, record.value === property)
    }
  })
}

function rampChanged(data: SelectItem) {
  selectChange(heightData.value, heightData.value[0].value)
  selectChange(rampData.value, String(data.value))
}

function heightChanged(data: SelectItem) {
  selectChange(rampData.value, rampData.value[0].value)
  selectChange(heightData.value, String(data.value))
}

function widthChanged(data: SelectItem) {
  selectChange(widthData.value, String(data.value))
}

function checkOnVisibleParam(key: string) {
  return visibleProperties.value.includes(key) || key === 'default'
}

function filterArray<T extends { value: string }> (array: T[]): T[] {
  return array.filter(item => checkOnVisibleParam(item.value))
}

async function updateVisibleProperties(zoneGuid: string) {
  visibleProperties.value = await getOrderSpecificLoadingList(zoneGuid)
}

async function onShow() {
  loading.value = true
  copyValue.value = { ...value.value }
  await updateVisibleProperties(locationZoneGuid.value)
  normalizeSpecificLoading()
  loading.value = false
}
</script>
